import { Empty, Space, Tag, Typography } from 'antd';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapContainer, Marker, Popup, TileLayer, Tooltip as LeafletTooltip } from 'react-leaflet';
import { NOC_SEMAPHORE_META, useThemeMode } from '@goelectric/ui-components';
import type { NocCoordinates, NocSemaphore, NocStationSummary } from '@goelectric/shared-types';
import { SemaphoreBadge, StationStateBadge } from './StatusBadge';
import { formatRelative, isDemoName } from '../utils/format';

const COLOMBIA_CENTER: [number, number] = [4.5709, -74.2973];
const COLOMBIA_ZOOM = 6;

type GeolocatedStation = NocStationSummary & { coordinates: NocCoordinates };

/** Escapa texto para incrustarlo en atributos HTML del `DivIcon`. */
function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Marcador circular coloreado por semáforo (sin assets de imagen de Leaflet). */
function markerIcon(color: string, highlighted: boolean, label: string): L.DivIcon {
  const size = highlighted ? 26 : 16;
  const border = highlighted ? 3 : 2;
  // Nombre accesible del marcador: Leaflet aplica `title` a la raíz del icono (tooltip
  // nativo, que duplicaría al `LeafletTooltip`) y no soporta `alt`/`aria-label` en
  // `DivIcon` por API (solo los asigna a iconos `<img>`). Sí inyecta este HTML dentro de
  // la raíz `role="button"`, así que un `role="img"` con `aria-label` aporta el nombre
  // sin tooltip nativo extra.
  return L.divIcon({
    className: 'noc-marker',
    html: `<span role="img" aria-label="${escapeAttr(label)}" style="display:block;width:${size}px;height:${size}px;border-radius:50%;background:${color};border:${border}px solid var(--ge-color-shell-text);box-shadow:0 0 0 2px ${color}66,0 1px 4px rgba(0,0,0,0.45);"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

interface StationMapProps {
  stations: NocStationSummary[];
  selectedStationId: string | null;
  onSelectStation: (stationId: string) => void;
}

/** Mapa de Colombia con una estación por marcador (semáforo + tooltip con estado/ciudad). */
export function StationMap({ stations, selectedStationId, onSelectStation }: StationMapProps) {
  const { resolvedTheme } = useThemeMode();
  const geolocated = stations.filter(
    (station): station is GeolocatedStation => station.coordinates !== null,
  );

  if (geolocated.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Sin estaciones geolocalizadas todavía.
            <br />
            Las coordenadas DEMO se cargan al aplicar la migración <code>0004_noc_demo_locations</code>.
          </Typography.Paragraph>
        }
      />
    );
  }

  const colorFor = (semaphore: NocSemaphore): string => {
    const meta = NOC_SEMAPHORE_META[semaphore];
    return resolvedTheme === 'dark' ? meta.colorDark : meta.color;
  };

  return (
    <div className="noc-map">
      <MapContainer
        center={COLOMBIA_CENTER}
        zoom={COLOMBIA_ZOOM}
        scrollWheelZoom
        className="noc-map__canvas"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {geolocated.map((station) => (
          <Marker
            key={station.stationId}
            position={[station.coordinates.lat, station.coordinates.lng]}
            icon={markerIcon(
              colorFor(station.semaphore),
              station.stationId === selectedStationId,
              `${station.name ?? station.stationId} — ${station.city ?? 'Sin ciudad'}`,
            )}
            eventHandlers={{ click: () => onSelectStation(station.stationId) }}
          >
            <LeafletTooltip direction="top" offset={[0, -10]} opacity={1}>
              <strong>{station.name ?? station.stationId}</strong>
              <br />
              {station.city ?? 'Sin ciudad'}
            </LeafletTooltip>
            <Popup>
              <div className="noc-popup">
                <Space direction="vertical" size={4}>
                  <strong>
                    {station.name ?? station.stationId} {isDemoName(station.name) && <Tag color="gold">DEMO</Tag>}
                  </strong>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {station.city ?? 'Sin ciudad'} · {station.stationId}
                  </Typography.Text>
                  <StationStateBadge state={station.state} forceDark />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {station.connectors.byState.AVAILABLE}/{station.connectors.total} conectores libres ·{' '}
                    {station.activeSessions} sesión(es) · {formatRelative(station.lastSeenAt)}
                  </Typography.Text>
                </Space>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      <Space className="noc-map__legend" size="large" wrap>
        <SemaphoreBadge semaphore="VERDE" />
        <SemaphoreBadge semaphore="ROJO" />
        <SemaphoreBadge semaphore="GRIS" />
      </Space>
    </div>
  );
}
